<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError }>()
const localePath = useLocalePath()
const { t } = useI18n()

const handleError = () => clearError({ redirect: localePath('/') })

const statusCode = computed(() => Number(props.error?.statusCode) || 500)
const isChunkError = computed(() =>
  /Failed to fetch dynamically imported module|Loading (chunk|CSS chunk).*failed|Importing a module script failed/i.test(String(props.error?.message || '')))
// Was der Besucher liest: bei einem nachgeladenen Chunk „wird neu geladen",
// bei jedem Serverfehler der neutrale Satz — nie die technische Meldung.
// Google hatte genau die als Beschreibung der Startseite indexiert
// („500. Failed to fetch dynamically imported module: …/_nuxt/….js").
// 4xx (Seite nicht gefunden) duerfen ihre eigene, lesbare Meldung zeigen.
const visibleMessage = computed(() => {
  if (isChunkError.value) return t('error.reloading')
  if (statusCode.value >= 500) return t('error.default_message')
  return props.error?.statusMessage || props.error?.message || t('error.default_message')
})
const technicalMessage = computed(() => (import.meta.dev ? String(props.error?.message || '') : ''))

// Eine Fehlerseite ist nie ein Suchergebnis: noindex, neutraler Titel,
// neutrale Beschreibung — auch dann, wenn der Fehler erst im Browser
// entsteht und Googles Renderer diese Seite statt der Startseite sieht.
const seoTitle = computed(() => `${statusCode.value} · ${t('error.default_message')}`)
const seoDescription = computed(() => t('error.default_message'))
useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  robots: 'noindex, nofollow',
})

// Stale-chunk recovery: a failed dynamic import after a deploy lands
// here as a 500 with a message like "Failed to fetch dynamically
// imported module: …/_nuxt/<old-hash>.js". Reload once to pull the
// fresh manifest. Mirrors the global handler in
// plugins/chunk-error-reload.client.ts but covers the case where the
// failure is caught by Nuxt's router and surfaced through error.vue
// before our `unhandledrejection` listener sees it.
if (import.meta.client) {
    const msg = String(props.error?.message || '')
    const looksLikeChunkError = /Failed to fetch dynamically imported module|Loading (chunk|CSS chunk).*failed|Importing a module script failed/i.test(msg)
    if (looksLikeChunkError) {
        const RELOAD_KEY = 'nuxt-chunk-reload'
        const last = sessionStorage.getItem(RELOAD_KEY)
        const now = Date.now()
        if (!last || now - Number(last) >= 10_000) {
            sessionStorage.setItem(RELOAD_KEY, String(now))
            console.warn('[chunk-error] reloading from error page:', msg)
            window.location.reload()
        }
    }
}
</script>

<template>
  <div class="error-page">
    <div class="glow-bg"></div>
    
    <div class="glass container error-card">
      <h1 class="error-code">{{ statusCode }}</h1>
      <p class="error-message">{{ visibleMessage }}</p>
      <p v-if="technicalMessage" class="error-technical">{{ technicalMessage }}</p>
      
      <button @click="handleError" class="btn btn-primary">
        {{ $t('error.back_home') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: var(--space-4);
}

.glow-bg {
  position: absolute;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, var(--primary-glow) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: -1;
  pointer-events: none;
}

.error-technical {
  font-size: 0.8rem;
  color: var(--text-dim);
  word-break: break-all;
}

.error-card {
  max-width: 500px;
  padding: var(--space-8);
  text-align: center;
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-6);
}

.error-code {
  font-family: var(--font-heading);
  font-size: 8rem;
  line-height: 1;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  text-shadow: 0 0 30px var(--primary-glow);
}

.error-message {
  font-size: 1.25rem;
  color: var(--text-muted);
  max-width: 80%;
  margin: 0 auto;
}
</style>